/**
 * Select.jsx
 *
 * @Author: jruif
 * @Date: 2018/1/21 上午10:12
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TagsInput from '../tagSelect/TagInput';
import Input from '../input';
import ExpandMore from '../icons/internals/ExpandMore';
import DropdownMenu from '../dropdownMenu';
// import AutoComplete from '../autoComplete';
import Menu, { MenuItem } from '../menu';

/**
 * Select
 * */
class Select extends React.Component {
  static propTypes = {
    /**
     * 占位描述字段
     * */
    placeholder: PropTypes.string,
    /**
     * 预设值
     * */
    defaultValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
    ]),
    /**
     * Select的选项
     * */
    options: PropTypes.array,
    /**
     * 是否禁止
     * */
    disabled: PropTypes.bool,
    /**
     * 是否过滤
     * */
    filterable: PropTypes.bool,
    /**
     * Select的ref函数，获取Select的dom元素
     * */
    selectRef: PropTypes.func,
    /**
     * 自定义样式
     * */
    style: PropTypes.object,
    /**
     * Input属性
     * */
    inputProps: PropTypes.object,
    /**
     * 是否多选
     * */
    multiple: PropTypes.bool,
    /**
     * 是否可搜索
     * */
    searchable: PropTypes.bool,
    /**
     * 是否根据输入项进行筛选
     * */
    filter: PropTypes.func,
    /**
     * 文本框值变化时回调
     * */
    onChange: PropTypes.func,
    /**
     * 选择框大小，默认medium，base同medium
     * */
    size: PropTypes.oneOf(['large', 'medium', 'small', 'base']), // base === medium
    /**
     * 在change返回值中包含label
     * */
    hasLabelInChange: PropTypes.bool,
    /**
     * 多选标签渲染方法
     */
    tagRender: PropTypes.bool,
    /**
     * 是否占满整行
     */
    fullWidth: PropTypes.bool,
    /**
     *  scrollContainer Tooltip兼容自定义可滚动容器
     */
    scrollContainer: PropTypes.node,
  };

  static defaultProps = {
    multiple: false,
    searchable: false,
    prefixCls: 'ne-select',
    filterable: false,
    disabled: false,
    size: 'medium',
    fullWidth: false,
    indent: 12,
    filter: (search, value) => {
      if (search === '') {
        return true;
      }
      return `${value}`.includes(search);
    },
  };

  // static getDerivedStateFromProps(props, state) {
  //   const { defaultChecked } = props;
  //   if (!state.isControlled && defaultChecked !== state.prevDefaultChecked) {
  //     return {
  //       checked: defaultChecked,
  //       prevDefaultChecked: defaultChecked,
  //     };
  //   }
  //   return null;
  // }

  constructor(props, context) {
    super(props, context);
    const { options, defaultValue, children, multiple } = props;
    options
      ? options.forEach(({ label, value }) => {
          this.valueToLabel[value] = label;
        })
      : React.Children.forEach(children, child => {
          if (!React.isValidElement(child)) {
            return null;
          }
          this.valueToLabel[child.props.value] = child.props.searchText || child.props.children;
        });

    let value = '';
    if (multiple && !Array.isArray(defaultValue)) {
      value = defaultValue == null ? [] : [defaultValue];
    } else {
      value = defaultValue;
    }
    this.state = {
      value,
      isOpen: false,
      search: props.multiple
        ? ''
        : this.valueToLabel[props.defaultValue] == null
        ? ''
        : this.valueToLabel[props.defaultValue],
    };
  }

  componentWillReceiveProps(nextProps) {
    const { defaultValue, children, options } = this.props;
    if (children !== nextProps.children || options !== nextProps.options) {
      this.valueToLabel = {};
      nextProps.options
        ? nextProps.options.forEach(({ label, value }) => {
            this.valueToLabel[value] = label;
          })
        : React.Children.forEach(nextProps.children, child => {
            if (!React.isValidElement(child)) {
              return null;
            }
            this.valueToLabel[child.props.value] = child.props.searchText || child.props.children;
          });
      if (!nextProps.multiple && !nextProps.needReceive) {
        this.setState(state => ({
          search: this.valueToLabel[state.value] == null ? '' : this.valueToLabel[state.value],
        }));
      }
    }
    if (nextProps.defaultValue !== defaultValue) {
      let value = '';
      if (nextProps.multiple) {
        if (!Array.isArray(nextProps.defaultValue)) {
          value = [nextProps.defaultValue];
        } else {
          value = nextProps.defaultValue;
        }
        this.setState(() => ({
          value,
          search: '',
        }));
      } else {
        value = nextProps.defaultValue;
        this.setState({
          value,
          search: this.valueToLabel[value] == null ? '' : this.valueToLabel[value],
        });
      }
    }
  }

  valueToLabel = {};

  getSelectWidth = isOpen => {
    const { disabled, searchable, multiple, options } = this.props;
    if (!isOpen || disabled) {
      this.setState({
        isOpen: false,
      });
      if (searchable && !multiple) {
        this.setState(state => {
          const target = this.getTagData(options, state.value);
          return {
            search: target == null ? '' : target.label,
          };
        });
      } else if (searchable) {
        this.setState({
          search: '',
        });
      }
      return;
    }

    if (searchable && !multiple) {
      this.setState({
        search: '',
      });
    }
    this.setState(() => ({
      isOpen: true,
    }));
  };

  handleChange = (e, value, itemValue) => {
    const { onChange, multiple, hasLabelInChange, options } = this.props;
    this.setState(() => ({
      search: !multiple ? this.valueToLabel[value] : '',
      value,
    }));
    onChange &&
      onChange(
        hasLabelInChange && !multiple ? options.find(item => item.value === value) : value,
        itemValue,
      );
  };

  handleSearch = (e, search) => {
    this.setState({
      search: search == null ? '' : search,
    });
    const { onSearch } = this.props;
    onSearch && onSearch(e, search);
  };

  getTagData = (options, value) => {
    const target = this.valueToLabel[value];
    return target ? { label: target, value } : '';
  };

  tagFilter = tag => {
    const { onChange } = this.props;
    const { value } = this.state;
    this.setState(state => ({
      value: state.value.filter(v => v !== tag),
    }));
    onChange && onChange(value.filter(v => v !== tag), tag);
  };

  render() {
    const {
      prefixCls,
      className,
      children,
      readOnly,
      defaultValue,
      placeholder,
      filterable,
      style,
      disabled,
      options: propOptions,
      inputProps,
      size,
      multiple,
      onChange,
      filter,
      onSearch,
      searchable,
      tagRender,
      fullWidth,
      status,
      errorIcon,
      loadingIcon,
      successIcon,
      id,
      showStatusIcon,
      scrollContainer,
      ...other
    } = this.props;
    const { isOpen, value, search } = this.state;
    const options = propOptions
      ? propOptions.map(({ label, ...item }, index) => (
          <MenuItem
            value={value}
            className={classNames(`${prefixCls}-menu-item`)}
            key={value || index}
            {...item}
            role="option">
            {label}
          </MenuItem>
        ))
      : React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) {
            return null;
          }
          return React.cloneElement(child, {
            key: child.props.value || index,
            role: 'option',
          });
        });
    const filtered = searchable
      ? options.filter(option =>
          filter(search, option.props.searchText || option.props.children, option.props.value),
        )
      : options;
    const valueData = this.getTagData(options, value);
    const inputClasses = classNames(prefixCls, className, `${prefixCls}-input`, {
      [`${prefixCls}-open`]: isOpen && !disabled,
      [`${prefixCls}-disabled`]: disabled,
      [`${prefixCls}-multiple`]: multiple,
    });
    return (
      <DropdownMenu
        autoWidth
        anchorEl={
          !multiple ? (
            <Input
              autoComplete="off"
              id={id}
              status={status}
              errorIcon={errorIcon}
              loadingIcon={loadingIcon}
              successIcon={successIcon}
              showStatusIcon={showStatusIcon}
              aria-haspopup="true"
              onChange={this.handleSearch}
              readOnly={!searchable || readOnly}
              size={size}
              suffix={<ExpandMore />}
              style={style}
              value={searchable ? search || '' : ''}
              mask={!searchable ? search : null}
              placeholder={placeholder}
              disabled={disabled}
              {...inputProps}
            />
          ) : (
            <TagsInput
              id={id}
              status={status}
              errorIcon={errorIcon}
              loadingIcon={loadingIcon}
              successIcon={successIcon}
              showStatusIcon={showStatusIcon}
              aria-haspopup="true"
              role="button"
              onTagDelete={this.tagFilter}
              onChange={this.handleSearch}
              searchText={search}
              readOnly={!searchable || readOnly}
              // onClick={this.handleOpen}
              tags={
                multiple
                  ? value
                      .filter(tag => this.getTagData(options, tag))
                      .map(tag => this.getTagData(options, tag))
                  : []
              }
              placeholder={placeholder}
              className={inputClasses}
              tagRender={tagRender}
              style={style}
              disabled={disabled}
              searchable={searchable}
              size={size}
              {...inputProps}
            />
          )
        }
        // trigger="click"
        trigger={multiple && searchable ? 'focus' : 'click'}
        disabled={disabled}
        onOpenChange={this.getSelectWidth}
        defaultIsOpen={isOpen}
        noClose={multiple}
        scrollContainer={scrollContainer}>
        <Menu
          {...other}
          selectable
          indentBase={0}
          multiple={multiple}
          onChange={this.handleChange}
          className={classNames(`${prefixCls}-menu`)}
          value={value}
          role="listbox"
          size={size}>
          {filtered}
        </Menu>
      </DropdownMenu>
    );
  }
}

export default Select;



// WEBPACK FOOTER //
// ./src/select/Select.jsx